import React from "react";

interface Props {
  isFlipped: boolean;
  front: React.ReactNode;
  back: React.ReactNode;
  /** When given, the card itself is a button that turns it over. */
  onFlip?: () => void;
  flipLabel?: string;
}

/**
 * CSS 3D flip wrapper. The parent must not have overflow:hidden.
 *
 * The box has the card's own proportion (85.6 × 54) and width, so both faces
 * fill it exactly. It used to be a fixed 230 px tall while the front was a
 * different height, and the front spilled out of its container onto the
 * buttons below.
 */
export const CardFlip: React.FC<Props> = ({
  isFlipped,
  front,
  back,
  onFlip,
  flipLabel,
}) => {
  const faces = (
    <div
      className="relative h-full w-full transition-transform duration-[450ms] ease-out motion-reduce:transition-none"
      style={{
        transformStyle: "preserve-3d",
        transform: isFlipped ? "rotateY(180deg)" : "rotateY(0deg)",
      }}
    >
      <div
        className="absolute inset-0"
        style={{
          backfaceVisibility: "hidden",
          WebkitBackfaceVisibility: "hidden",
        }}
        aria-hidden={isFlipped}
      >
        {front}
      </div>
      <div
        className="absolute inset-0"
        style={{
          backfaceVisibility: "hidden",
          WebkitBackfaceVisibility: "hidden",
          transform: "rotateY(180deg)",
        }}
        aria-hidden={!isFlipped}
      >
        {back}
      </div>
    </div>
  );

  return (
    <div
      className="relative w-full max-w-[342px] aspect-[85.6/54]"
      style={{ perspective: "1000px" }}
    >
      {onFlip ? (
        <button
          type="button"
          onClick={onFlip}
          aria-label={flipLabel}
          className="block h-full w-full rounded-[18px] p-0 text-left"
        >
          {faces}
        </button>
      ) : (
        faces
      )}
    </div>
  );
};
